import _objectSpread from "/home/jenkins/workspace/tServices_Forms_Client_CICD_main/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectSpread2";
import _defineProperty from "/home/jenkins/workspace/tServices_Forms_Client_CICD_main/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "/home/jenkins/workspace/tServices_Forms_Client_CICD_main/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "className", "error", "hideLabel", "label", "placeholder", "defaultValue", "value", "onValueChange", "defaultOpen", "open", "onOpenChange", "name", "disabled", "readonly", "subText", "required", "id"],
    _excluded2 = ["children", "className", "container"],
    _excluded3 = ["text", "secondaryText"];
import { jsx, jsxs } from 'react/jsx-runtime';
import { PencilCrossed, Caret, Checkmark } from '@box/blueprint-web-assets/icons/Fill';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import * as SelectPrimitive from '@radix-ui/react-select';
import clsx from 'clsx';
import { forwardRef, useCallback } from 'react';
import { InlineError } from '../primitives/inline-error/inline-error.js';
import { Text } from '../text/text.js';
import { useLabelable } from '../util-components/labelable/useLabelable.js';
import { useUniqueId } from '../utils/useUniqueId.js';
import styles from './select.module.js';
var Root = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var _clsx3;

  var children = props.children,
      className = props.className,
      error = props.error,
      hideLabel = props.hideLabel,
      label = props.label,
      placeholder = props.placeholder,
      defaultValue = props.defaultValue,
      value = props.value,
      onValueChange = props.onValueChange,
      defaultOpen = props.defaultOpen,
      open = props.open,
      onOpenChange = props.onOpenChange,
      name = props.name,
      disabled = props.disabled,
      readonly = props.readonly,
      subText = props.subText,
      required = props.required,
      id = props.id,
      triggerProps = _objectWithoutProperties(props, _excluded);

  var selectId = useUniqueId('select-', !id) || id || 'default-select-id';
  var hasError = !!error && !disabled; // Restrict consumers from opening options menu when Root is disabled

  var isSelectDefaultOpen = defaultOpen && !disabled;
  var isSelectOpen = open && !disabled;

  var handleOnChange = function handleOnChange(isOpen) {
    if (!readonly) {
      onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(isOpen);
    }
  };

  var selectProps = {
    name: name,
    disabled: disabled,
    required: required,
    value: value,
    defaultValue: defaultValue,
    defaultOpen: !readonly && isSelectDefaultOpen,
    open: !readonly && isSelectOpen,
    onValueChange: onValueChange,
    onOpenChange: handleOnChange
  };
  var Label = useLabelable(label, selectId);
  var inlineErrorId = useUniqueId('inline-error-');
  var ariaDescribedBy = clsx(triggerProps['aria-describedby'], _defineProperty({}, inlineErrorId, hasError));
  var icon = readonly ? jsx(PencilCrossed, {
    className: styles.iconPencilCrossed
  }) : jsx(Caret, {
    className: styles.iconCaret
  });
  return jsxs("div", {
    className: clsx(styles.container, _defineProperty({}, styles.disabled, disabled), className),
    children: [jsx(Label, {
      className: styles.label,
      hideLabel: hideLabel
    }), jsxs(SelectPrimitive.Root, _objectSpread(_objectSpread({}, selectProps), {}, {
      children: [jsxs(SelectPrimitive.Trigger, _objectSpread(_objectSpread(_objectSpread({}, triggerProps), {}, {
        ref: forwardedRef,
        "aria-describedby": ariaDescribedBy
      }, hasError && {
        'aria-invalid': 'true'
      }), {}, {
        "aria-readonly": readonly ? 'true' : undefined,
        className: clsx(styles.triggerBtn, (_clsx3 = {}, _defineProperty(_clsx3, styles.error, hasError), _defineProperty(_clsx3, styles.readonly, readonly), _clsx3)),
        id: selectId,
        children: [jsx(SelectPrimitive.Value, {
          placeholder: placeholder
        }), jsx(SelectPrimitive.Icon, {
          className: styles.iconWrapper,
          children: icon
        })]
      })), children]
    })), jsx(InlineError, {
      className: styles.inlineError,
      id: inlineErrorId,
      children: error
    }), readonly && subText && jsx(Text, {
      as: "p",
      color: "textOnLightSecondary",
      children: subText
    })]
  });
});
Root.displayName = 'Select';
var Content = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var children = props.children,
      className = props.className,
      container = props.container,
      contentProps = _objectWithoutProperties(props, _excluded2);

  return jsx(SelectPrimitive.Portal, {
    container: container,
    children: jsx(SelectPrimitive.Content, _objectSpread(_objectSpread({}, contentProps), {}, {
      ref: forwardedRef,
      className: clsx(styles.content, className),
      position: "popper",
      sideOffset: 8,
      children: jsx(ScrollArea.Root, {
        type: "auto",
        children: jsx(SelectPrimitive.Viewport, {
          asChild: true,
          className: styles.viewport,
          children: jsx(ScrollArea.Viewport, {
            children: children
          })
        })
      })
    }))
  });
});
Content.displayName = 'Select.Content';
var Option = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var text = props.text,
      secondaryText = props.secondaryText,
      itemProps = _objectWithoutProperties(props, _excluded3);

  var onPointerLeave = useCallback(function (event) {
    return event.preventDefault();
  }, []);
  var onPointerMove = useCallback(function (event) {
    return event.preventDefault();
  }, []);
  return jsxs(SelectPrimitive.Item, _objectSpread(_objectSpread({}, itemProps), {}, {
    ref: forwardedRef,
    className: styles.option,
    onPointerLeave: onPointerLeave,
    onPointerMove: onPointerMove,
    children: [jsx(SelectPrimitive.ItemText, {
      children: text
    }), !!secondaryText && jsx("span", {
      className: styles.secondaryText,
      children: secondaryText
    }), !itemProps.disabled && jsx(SelectPrimitive.ItemIndicator, {
      className: styles.indicator,
      children: jsx(Checkmark, {})
    })]
  }));
});
Option.displayName = 'Select.Option';
var Separator = /*#__PURE__*/forwardRef(function (_, forwardedRef) {
  return jsx(SelectPrimitive.Separator, {
    ref: forwardedRef,
    className: styles.separator
  });
});
Separator.displayName = 'Select.Separator';
/**
 * The root component that contains all the parts of a select.
 *
 * @example
 * const [value, setValue] = React.useState('');
 *
 * const label = 'Select';
 * const placeholder = 'Select an option';
 *
 * <Select label={label} onValueChange={setValue} placeholder={placeholder} value={value}>
 *     <Select.Option text="Option 1" value="option-1" />
 *     <Select.Option text="Option 2" value="option-2" />
 *
 *     <Select.Separator />
 *
 *     <Select.Option text="Option 3" value="option-3" />
 *     <Select.Option text="Option 4" value="option-4" />
 * </Select>
 */

var Select = Object.assign(Root, {
  /**
   * The component that pops out when the select is open.
   */
  Content: Content,

  /**
   * The component that displays a select option.
   */
  Option: Option,

  /**
   * The component used to visually separate options in the select.
   */
  Separator: Separator
});
/**
 * This can be used as a symbol to set a value for an option that serves as a placeholder.
 * With additional handling added to onValueChange, it can be used to clear the value.
 *
 * See: https://github.com/radix-ui/primitives/issues/2706 for more details.
 */

var SELECT_EMPTY_VALUE = '__empty__';
export { SELECT_EMPTY_VALUE, Select };